import { useApi } from '@/shared/api/http';
import type {
  IScheduleDay,
  ITimeSlot,
  ICreateOrderData,
  ICreateOrderResponse,
} from '@/entities/appointment/model/types';
import type {
  IOrder,
  IGetOrdersParams,
  IUpdateOrderData,
  IOwnScheduleDay,
  IUpdateScheduleSlotData,
  IToggleScheduleDayData,
  IUpdateScheduleDayTypeData,
} from '@/entities/appointment/model/types/order';

export const useAppointmentService = () => {
  const api = useApi();

  const getThreeScheduleDays = async (lawyerId: number) => {
    return await api.get<IScheduleDay[]>(`appointment/${lawyerId}/three-days/`);
  };

  const getScheduleMonth = async (lawyerId: number, year: number, month: number) => {
    return await api.get<IScheduleDay[]>(`appointment/${lawyerId}/${year}/${month}/`);
  };

  const getScheduleDay = async (
    lawyerId: number,
    year: number,
    month: number,
    day: number,
  ) => {
    return await api.get<ITimeSlot[]>(`appointment/${lawyerId}/${year}/${month}/${day}/`);
  };

  const createOrder = async (data: ICreateOrderData) => {
    return await api.post<ICreateOrderResponse>('legal-services-order', data);
  };

  const getSelfOrders = async (params: IGetOrdersParams) => {
    return await api.get<IOrder[]>('legal-services-order/self/', {
      params: { ...params },
    });
  };

  const getClientOrders = async (params: IGetOrdersParams) => {
    return await api.get<IOrder[]>('legal-services-order/', {
      params: { ...params },
    });
  };

  const updateOrder = async (orderId: number, data: IUpdateOrderData) => {
    return await api.patch(`legal-services-order/${orderId}`, data);
  };

  const getOwnSchedule = async (year: number, month: number) => {
    return await api.post<IOwnScheduleDay[]>(`schedule/${year}/${month}/`, {});
  };

  const updateScheduleSlot = async (data: IUpdateScheduleSlotData) => {
    return await api.patch('schedule/timestamp', data);
  };

  const toggleScheduleDay = async (data: IToggleScheduleDayData) => {
    return await api.patch('schedule/day', data);
  };

  const updateScheduleDayType = async (data: IUpdateScheduleDayTypeData) => {
    return await api.patch('schedule/day/type', data);
  };

  return {
    getThreeScheduleDays,
    getScheduleMonth,
    getScheduleDay,
    createOrder,
    getSelfOrders,
    getClientOrders,
    updateOrder,
    getOwnSchedule,
    updateScheduleSlot,
    toggleScheduleDay,
    updateScheduleDayType,
  };
};
